//TYPE
import Head from 'next/head'
import Layout from '../../layouts'

//GRAPHQL

import { getCMSImageUrl } from '@/utils/index'

//COMPONENTS
import { EContentMode, PAGE_TYPE } from '@/ts/constant'
import { DOMAIN } from '@/data/metadata'
import { useRouter } from 'next/router'
import {
  Get_Page_With_FilterQuery,
  Get_Post_With_FilterQuery,
  Get_Post_With_FilterQueryVariables,
  Pages,
  Post,
  Tag,
} from '@/ts/graphqlschema'
import { GET_POST_WITH_FILTER } from '@/graphql/queries/post'
import { formatPostMetaData } from '@/utils/format-metadata'
import { GalleryHeader, PostBody } from '@/components/sections/post-detail'
import Image from 'next/image'
import { GetStaticPaths, GetStaticProps } from 'next'
import { clientSecret } from '@/graphql/apollo-client-secret'
import React, { FC } from 'react'
import { GET_PAGE_WITH_FILTER } from '@/graphql/queries/page/get_page_by_slug'
import fetchDataForHTMLBuilder from '@/utils/fetch-html-builder-data'
import GrapeParser from '@/components/GrapeParser'
import { GalleryImageVariant1 } from '@/components/sections/gallery-image'

type BlogDetailProps = {
  post: Post
  pageData: Pages
  globalData?: any
  extra?: any
  tags: Tag[]
}

const BlogDetail: FC<BlogDetailProps> = props => {
  const { post, pageData, globalData } = props
  const router = useRouter()
  const canonicalUrl = `${DOMAIN}/room/${router.query.slug}`

  const content = JSON.parse(post.newcontent)
  const css = content?.css
  const component = content?.projectData?.pages?.[0]?.frames?.[0]?.component

  //above & below content
  const above_content =
    pageData?.above_content && JSON.parse(pageData?.above_content?.content)
  const below_content =
    pageData?.below_content && JSON.parse(pageData?.below_content?.content)
  const above_content_css = above_content?.css
  const below_content_css = below_content?.css
  const above_content_component =
    above_content?.projectData?.pages?.[0]?.frames?.[0]?.component
  const below_content_component =
    below_content?.projectData?.pages?.[0]?.frames?.[0]?.component

  return (
    <Layout
      shouldRenderContacts
      shouldRenderHighlight
      notUseScrollAnimation
      metadata={formatPostMetaData({ ...post, canonicalUrl })}
    >
      <Head>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      {/*Above Content*/}
      <div>
        {above_content_component && (
          <GrapeParser component={above_content_component} globalData={{}} />
        )}
        {above_content_css && (
          <style dangerouslySetInnerHTML={{ __html: above_content_css }} />
        )}
      </div>

      {post.content_mode === EContentMode.GRAPEJS && (
        <section className="content-body-grapejs">
          {component && (
            <GrapeParser
              component={component}
              currentItem={post}
              globalData={globalData}
            />
          )}
          {css && <style dangerouslySetInnerHTML={{ __html: css }} />}
        </section>
      )}

      {post.content_mode === EContentMode.TIPTAP && (
        <>
          <GalleryHeader post={post} />
          <section className="content-body-grapejs">
            <section
              className="py-6 top-24 wrap-content relative"
              id="blog-content"
            >
              <div className="blog-heading-image__container box-utility">
                <div className="blog-heading-image">
                  <Image
                    src={getCMSImageUrl(post?.thumbnail, 'lg')}
                    alt={post?.thumbnail?.description}
                    objectFit="cover"
                    layout="fill"
                  />
                </div>
              </div>
              <div className="page-vertical-padding-small mx-auto">
                <div className="">
                  <div className="blog-content-wrapper">
                    <PostBody content={post?.blog_content} />
                    <div className="page-vertical-padding-small">
                      <hr className="border-t-2 border-solid w-full border-gray-3" />
                    </div>
                  </div>

                  <div className="gallery-content-wrapper">
                    {post?.gallery && (
                      <GalleryImageVariant1 gallery={post.gallery} />
                    )}
                  </div>
                  {/* SHARE */}
                  <div className="text-center">
                    <p className="text-xl font-medium uppercase">Share</p>
                  </div>
                </div>
              </div>
            </section>
          </section>
        </>
      )}

      {/*Below Content*/}
      <div>
        {below_content_component && (
          <GrapeParser component={below_content_component} globalData={{}} />
        )}
        {below_content_css && (
          <style dangerouslySetInnerHTML={{ __html: below_content_css }} />
        )}
      </div>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const postSlug = params?.slug?.toString()

  if (postSlug) {
    /**
     * Get All Tours filter by status
     */
    const params = {
      post_filter: {
        status: {
          _eq: 'published',
        },
        slug: {
          _eq: postSlug,
        },
      },
    }
    try {
      const { data } = await clientSecret.query<
        Get_Post_With_FilterQuery,
        Get_Post_With_FilterQueryVariables
      >({
        query: GET_POST_WITH_FILTER,
        variables: params,
        fetchPolicy: 'network-only',
      })
      const post = data.post[0]

      /**
       * GET POST TEMPLATE PAGE
       */
      const queryParams = {
        page_filter: {
          page_type: {
            _eq: PAGE_TYPE.DetailPost,
          },
        },
      }
      const { data: detailPostPageData } =
        await clientSecret.query<Get_Page_With_FilterQuery>({
          query: GET_PAGE_WITH_FILTER,
          variables: queryParams,
          fetchPolicy: 'network-only',
        })
      const pageData = detailPostPageData.pages[0]

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const content = JSON.parse(post?.newcontent)
      const component = content?.projectData?.pages?.[0]?.frames?.[0]?.component

      const globalData = {}
      await fetchDataForHTMLBuilder(component, globalData)

      return {
        props: {
          post,
          pageData,
          globalData,
        },
        revalidate: 15,
      }
    } catch (e) {}
  }

  return {
    redirect: {
      destination: '/rooms',
      permanent: false,
    },
    revalidate: 15,
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export const getStaticPaths: GetStaticPaths = async () => {
  const params = {
    post_filter: {
      status: {
        _eq: 'published',
      },
    },
    sort: '-publish_date',
  }
  const { data, error } = await clientSecret.query<
    Get_Post_With_FilterQuery,
    Get_Post_With_FilterQueryVariables
  >({
    query: GET_POST_WITH_FILTER,
    variables: params,
    fetchPolicy: 'network-only',
  })
  // Get the paths we want to pre-render based on posts
  const paths =
    error || !data?.post
      ? []
      : data?.post?.map(post => ({
          params: { slug: post.slug },
          // params: { pid: detail.id },
        }))
  // We'll pre-render only these paths at build time.
  // { fallback: blocking } will server-render pages
  // on-demand if the path doesn't exist.
  return { paths, fallback: 'blocking' }
}

export default BlogDetail
